import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ChevronRight, CheckCircle, XCircle, HelpCircle, RotateCcw, MessageCircle, Send, Loader2, X } from 'lucide-react';
import { useCourse } from '../hooks/useCourse';
import { useAuth } from '../hooks/useAuth';
import { MathKeyboard } from './MathKeyboard';
import { logActivity, saveAiFeedback, saveFlashcardResult, getFlashcardProgress } from '../lib/supabase';
import { MathExercise as MathExerciseType, MathStep, MathInputField, MathDiagram } from '../types';
import { callClaude } from '../lib/claude';

// ============ SOUND ============
const audioContext = typeof window !== 'undefined'
  ? new (window.AudioContext || (window as typeof window & { webkitAudioContext: typeof AudioContext }).webkitAudioContext)()
  : null;

function playSound(type: 'correct' | 'wrong' | 'complete') {
  if (!audioContext) return;
  if (audioContext.state === 'suspended') audioContext.resume();
  const now = audioContext.currentTime;

  if (type === 'complete') {
    [523, 659, 784].forEach((freq, i) => {
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      osc.connect(gain);
      gain.connect(audioContext.destination);
      osc.frequency.setValueAtTime(freq, now + i * 0.1);
      gain.gain.setValueAtTime(0.12, now + i * 0.1);
      gain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.1 + 0.3);
      osc.start(now + i * 0.1);
      osc.stop(now + i * 0.1 + 0.3);
    });
    return;
  }

  const osc = audioContext.createOscillator();
  const gain = audioContext.createGain();
  osc.connect(gain);
  gain.connect(audioContext.destination);

  if (type === 'correct') {
    osc.frequency.setValueAtTime(600, now);
    osc.frequency.setValueAtTime(800, now + 0.05);
    gain.gain.setValueAtTime(0.15, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.1);
    osc.start(now);
    osc.stop(now + 0.1);
  } else {
    osc.frequency.setValueAtTime(300, now);
    gain.gain.setValueAtTime(0.1, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.1);
    osc.start(now);
    osc.stop(now + 0.1);
  }
}

// ============ MATH EXPRESSION EVALUATOR ============
function evaluateMathExpression(expr: string): number | null {
  try {
    let e = expr
      .trim()
      .replace(/\s+/g, '')
      .replace(/,/g, '.')
      .replace(/·/g, '*')
      .replace(/×/g, '*')
      .replace(/÷/g, '/')
      .replace(/π/g, `${Math.PI}`)
      .replace(/(\d)(\()/g, '$1*(')
      .replace(/\)(\d)/g, ')*$1')
      .replace(/\)\(/g, ')*(');

    e = e.replace(/²/g, '**2').replace(/³/g, '**3');
    e = e.replace(/\^/g, '**');
    e = e.replace(/∛(\d+\.?\d*)/g, '($1)**(1/3)');
    e = e.replace(/√(\d+\.?\d*)/g, 'Math.sqrt($1)');

    if (!/^[\d+\-*/().eE\s]+$/.test(e.replace(/Math\.sqrt/g, '').replace(/\*\*/g, ''))) {
      return null;
    }

    const result = new Function(`"use strict"; return (${e})`)() as number;
    if (typeof result === 'number' && isFinite(result)) return result;
    return null;
  } catch {
    return null;
  }
}

// ============ MATH ANSWER MATCHING ============
function matchMathAnswer(userInput: string, correctValue: string, acceptedAnswers: string[]): boolean {
  const normalize = (s: string) => s
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/,/g, '.')
    .replace(/·/g, '*')
    .replace(/×/g, '*')
    .replace(/÷/g, '/')
    .replace(/π/g, 'pi')
    .replace(/²/g, '2')
    .replace(/³/g, '3');

  const normalizedInput = normalize(userInput);
  if (!normalizedInput) return false;

  for (const answer of acceptedAnswers) {
    if (normalize(answer) === normalizedInput) return true;
  }

  const numInput = parseFloat(normalizedInput);
  const numCorrect = parseFloat(normalize(correctValue));
  if (!isNaN(numInput) && !isNaN(numCorrect) && numCorrect !== 0) {
    const tolerance = Math.abs(numCorrect) * 0.015;
    if (Math.abs(numInput - numCorrect) <= tolerance) return true;
  }

  const evaluated = evaluateMathExpression(userInput);
  if (evaluated !== null && !isNaN(numCorrect) && numCorrect !== 0) {
    const tolerance = Math.abs(numCorrect) * 0.015;
    if (Math.abs(evaluated - numCorrect) <= tolerance) return true;
  }

  return false;
}

// ============ SVG DIAGRAMS ============
function MathDiagramRenderer({ diagram }: { diagram: MathDiagram }) {
  if (diagram.type === 'kugel') {
    return (
      <svg viewBox="0 0 200 165" style={{ maxWidth: 180, margin: '12px auto', display: 'block' }}>
        <circle cx="100" cy="72" r="55" fill="#42a5f5" fillOpacity="0.08" stroke="#42a5f5" strokeWidth="1.5"/>
        <ellipse cx="100" cy="72" rx="55" ry="18" fill="none" stroke="#42a5f5" strokeWidth="0.5" strokeDasharray="4 3" opacity="0.4"/>
        <circle cx="100" cy="72" r="3" fill="#ffd54f"/>
        <text x="100" y="65" fill="#9fa8da" fontSize="10" textAnchor="middle">M</text>
        <line x1="100" y1="72" x2="155" y2="72" stroke="#ffd54f" strokeWidth="1.5" strokeDasharray="4 2"/>
        <text x="128" y="65" fill="#ffd54f" fontSize="11" textAnchor="middle">r = {diagram.labels?.r || '?'}</text>
        {diagram.diameter && (
          <>
            <line x1="45" y1="140" x2="155" y2="140" stroke="#9fa8da" strokeWidth="1"/>
            <line x1="45" y1="135" x2="45" y2="145" stroke="#9fa8da" strokeWidth="1"/>
            <line x1="155" y1="135" x2="155" y2="145" stroke="#9fa8da" strokeWidth="1"/>
            <text x="100" y="155" fill="#9fa8da" fontSize="11" textAnchor="middle">d = {diagram.labels?.d}</text>
          </>
        )}
        {diagram.labels?.O && (
          <text x="100" y="155" fill="#42a5f5" fontSize="10" textAnchor="middle">O = {diagram.labels.O}</text>
        )}
        {diagram.labels?.V && (
          <text x="100" y="155" fill="#42a5f5" fontSize="10" textAnchor="middle">V = {diagram.labels.V}</text>
        )}
      </svg>
    );
  }

  if (diagram.type === 'halbkugel') {
    return (
      <svg viewBox="0 0 200 130" style={{ maxWidth: 180, margin: '12px auto', display: 'block' }}>
        <path d="M 40 75 A 60 60 0 0 1 160 75" fill="#42a5f5" fillOpacity="0.08" stroke="#42a5f5" strokeWidth="1.5"/>
        <line x1="40" y1="75" x2="160" y2="75" stroke="#42a5f5" strokeWidth="1.5"/>
        <ellipse cx="100" cy="75" rx="60" ry="15" fill="none" stroke="#42a5f5" strokeWidth="0.5" strokeDasharray="4 3" opacity="0.3"/>
        <circle cx="100" cy="75" r="3" fill="#ffd54f"/>
        <line x1="100" y1="75" x2="160" y2="75" stroke="#ffd54f" strokeWidth="1.5" strokeDasharray="4 2"/>
        <text x="130" y="68" fill="#ffd54f" fontSize="11" textAnchor="middle">r = {diagram.labels?.r || '?'}</text>
      </svg>
    );
  }

  if (diagram.type === 'zylinder-halbkugel') {
    return (
      <svg viewBox="0 0 200 200" style={{ maxWidth: 180, margin: '12px auto', display: 'block' }}>
        <path d="M 55 70 A 45 45 0 0 1 145 70" fill="#7c4dff" fillOpacity="0.08" stroke="#7c4dff" strokeWidth="1.5"/>
        <rect x="55" y="70" width="90" height="90" fill="#42a5f5" fillOpacity="0.08" stroke="#42a5f5" strokeWidth="1.5"/>
        <line x1="55" y1="70" x2="145" y2="70" stroke="#ff9800" strokeWidth="1" strokeDasharray="4 3"/>
        <ellipse cx="100" cy="160" rx="45" ry="12" fill="none" stroke="#42a5f5" strokeWidth="0.8" opacity="0.5"/>
        <line x1="100" y1="70" x2="145" y2="70" stroke="#ffd54f" strokeWidth="1" strokeDasharray="3 2"/>
        <text x="123" y="63" fill="#ffd54f" fontSize="10" textAnchor="middle">r = {diagram.labels?.r}</text>
        <line x1="155" y1="70" x2="155" y2="160" stroke="#9fa8da" strokeWidth="1"/>
        <line x1="150" y1="70" x2="160" y2="70" stroke="#9fa8da" strokeWidth="0.5"/>
        <line x1="150" y1="160" x2="160" y2="160" stroke="#9fa8da" strokeWidth="0.5"/>
        <text x="170" y="120" fill="#9fa8da" fontSize="10" textAnchor="middle">h = {diagram.labels?.h}</text>
      </svg>
    );
  }

  if (diagram.type === 'hohlkugel') {
    return (
      <svg viewBox="0 0 200 160" style={{ maxWidth: 180, margin: '12px auto', display: 'block' }}>
        <circle cx="100" cy="75" r="55" fill="#42a5f5" fillOpacity="0.06" stroke="#42a5f5" strokeWidth="1.5"/>
        <circle cx="100" cy="75" r="40" fill="#0a0e27" stroke="#ff9800" strokeWidth="1.5" strokeDasharray="4 3"/>
        <circle cx="100" cy="75" r="3" fill="#ffd54f"/>
        <line x1="100" y1="75" x2="155" y2="75" stroke="#42a5f5" strokeWidth="1" strokeDasharray="3 2"/>
        <text x="130" y="68" fill="#42a5f5" fontSize="10" textAnchor="middle">R = {diagram.labels?.R}</text>
        <line x1="100" y1="75" x2="100" y2="35" stroke="#ff9800" strokeWidth="1" strokeDasharray="3 2"/>
        <text x="88" y="52" fill="#ff9800" fontSize="10" textAnchor="end">r = {diagram.labels?.r}</text>
      </svg>
    );
  }

  if (diagram.type === 'boxplot' && diagram.boxplotValues) {
    const v = diagram.boxplotValues;
    const range = v.max - v.min || 1;
    const pad = 50;
    const w = 300;
    const toX = (val: number) => pad + ((val - v.min) / range) * w;

    return (
      <svg viewBox="0 0 400 100" style={{ maxWidth: 360, margin: '12px auto', display: 'block' }}>
        <line x1={pad - 10} y1="50" x2={pad + w + 10} y2="50" stroke="#9fa8da" strokeWidth="0.5"/>
        {[v.min, v.q1, v.median, v.q3, v.max].map((val, i) => (
          <g key={i}>
            <line x1={toX(val)} y1="47" x2={toX(val)} y2="53" stroke="#9fa8da" strokeWidth="1"/>
            <text x={toX(val)} y="68" fill="#9fa8da" fontSize="10" textAnchor="middle">{val}</text>
          </g>
        ))}
        <rect x={toX(v.q1)} y="30" width={toX(v.q3) - toX(v.q1)} height="20" rx="2"
              fill="#ff9800" fillOpacity="0.15" stroke="#ff9800" strokeWidth="1.5"/>
        <line x1={toX(v.median)} y1="30" x2={toX(v.median)} y2="50" stroke="#ffd54f" strokeWidth="2.5"/>
        <line x1={toX(v.min)} y1="40" x2={toX(v.q1)} y2="40" stroke="#ff9800" strokeWidth="1.5"/>
        <line x1={toX(v.q3)} y1="40" x2={toX(v.max)} y2="40" stroke="#ff9800" strokeWidth="1.5"/>
        <line x1={toX(v.min)} y1="35" x2={toX(v.min)} y2="45" stroke="#ff9800" strokeWidth="1.5"/>
        <line x1={toX(v.max)} y1="35" x2={toX(v.max)} y2="45" stroke="#ff9800" strokeWidth="1.5"/>
        <text x={toX(v.min)} y="85" fill="#9fa8da" fontSize="9" textAnchor="middle">Min</text>
        <text x={toX(v.q1)} y="24" fill="#ff9800" fontSize="9" textAnchor="middle">q₁</text>
        <text x={toX(v.median)} y="24" fill="#ffd54f" fontSize="9" textAnchor="middle">q₂</text>
        <text x={toX(v.q3)} y="24" fill="#ff9800" fontSize="9" textAnchor="middle">q₃</text>
        <text x={toX(v.max)} y="85" fill="#9fa8da" fontSize="9" textAnchor="middle">Max</text>
      </svg>
    );
  }

  if (diagram.type === 'kreisdiagramm' && diagram.data && diagram.dataLabels) {
    const total = diagram.data.reduce((a, b) => a + b, 0);
    const colors = ['#42a5f5', '#4caf50', '#ff9800', '#e91e63', '#9c27b0', '#00bcd4'];
    let currentAngle = -90;

    return (
      <svg viewBox="0 0 240 140" style={{ maxWidth: 220, margin: '12px auto', display: 'block' }}>
        {diagram.data.map((val, i) => {
          const angle = (val / total) * 360;
          const startAngle = currentAngle;
          currentAngle += angle;
          const endAngle = currentAngle;
          const x1 = 80 + 50 * Math.cos((startAngle * Math.PI) / 180);
          const y1 = 60 + 50 * Math.sin((startAngle * Math.PI) / 180);
          const x2 = 80 + 50 * Math.cos((endAngle * Math.PI) / 180);
          const y2 = 60 + 50 * Math.sin((endAngle * Math.PI) / 180);
          const largeArc = angle > 180 ? 1 : 0;
          return (
            <path
              key={i}
              d={`M 80 60 L ${x1} ${y1} A 50 50 0 ${largeArc} 1 ${x2} ${y2} Z`}
              fill={colors[i % colors.length]}
              fillOpacity="0.25"
              stroke={colors[i % colors.length]}
              strokeWidth="1"
            />
          );
        })}
        {diagram.dataLabels.map((label, i) => (
          <g key={i}>
            <rect x="155" y={15 + i * 18} width="10" height="10" rx="2" fill={colors[i % colors.length]} fillOpacity="0.5"/>
            <text x="170" y={24 + i * 18} fill="#9fa8da" fontSize="10">{label} ({diagram.data![i]})</text>
          </g>
        ))}
      </svg>
    );
  }

  return null;
}

// ============ AI CHAT PANEL ============
interface AiChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

function AiChatPanel({
  exercise,
  courseId,
  userId,
  onClose,
}: {
  exercise: MathExerciseType;
  courseId: string;
  userId: string;
  onClose: () => void;
}) {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<AiChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    // Focus input on open
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const question = input.trim();
    if (!question || isLoading) return;

    setMessages(prev => [...prev, { role: 'user', content: question }]);
    setInput('');
    setIsLoading(true);

    try {
      const systemPrompt = exercise.aiPrompt ||
        `Du hilfst einer Schülerin (4. Klasse Mittelschule) bei einer Mathe-Aufgabe. Aufgabe: "${exercise.instruction}". Erkläre Schritt für Schritt in einfacher Sprache. Gib NICHT die Lösung direkt — hilf den Weg zu finden.`;

      const apiMessages = [
        ...messages.map(m => ({ role: m.role, content: m.content })),
        { role: 'user' as const, content: question },
      ];

      const data = await callClaude({
        model: 'claude-sonnet-4-20250514',
        max_tokens: 600,
        system: systemPrompt,
        messages: apiMessages,
      }, 'math-help');
      const assistantText = data.content?.[0]?.text || 'Entschuldigung, ich konnte keine Antwort generieren.';
      setMessages(prev => [...prev, { role: 'assistant', content: assistantText }]);

      // Log to ai_feedback
      await saveAiFeedback(
        userId,
        'math_exercise_help',
        assistantText,
        { exercise_id: exercise.id, exercise_title: exercise.title },
        {
          courseId,
          question,
          userAnswer: '',
          scoreInfo: `Aufgabe: ${exercise.title}`,
        }
      );

      // Log to activity_log
      await logActivity(userId, 'math_ai_help', {
        course_id: courseId,
        exercise_id: exercise.id,
        exercise_title: exercise.title,
        question,
      });
    } catch (error) {
      console.error('Claude API error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Entschuldigung, es gab einen Fehler. Bitte versuche es nochmal.',
      }]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  return (
    <div style={{
      marginTop: '12px',
      background: 'rgba(124, 77, 255, 0.08)',
      border: '1px solid rgba(124, 77, 255, 0.25)',
      borderRadius: '12px',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 14px',
        background: 'rgba(124, 77, 255, 0.1)',
        borderBottom: '1px solid rgba(124, 77, 255, 0.15)',
      }}>
        <span style={{ color: '#b388ff', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
          <MessageCircle size={14} /> Frag Claude
        </span>
        <button
          onClick={onClose}
          style={{
            background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)',
            cursor: 'pointer', padding: '4px', display: 'flex',
          }}
        >
          <X size={16} />
        </button>
      </div>

      {/* Messages */}
      <div style={{
        maxHeight: '250px',
        overflowY: 'auto',
        padding: '10px 14px',
      }}>
        {messages.length === 0 && (
          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', textAlign: 'center', padding: '12px 0' }}>
            Stell deine Frage zu dieser Aufgabe...
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} style={{
            marginBottom: '10px',
            textAlign: msg.role === 'user' ? 'right' : 'left',
          }}>
            <div style={{
              display: 'inline-block',
              maxWidth: '85%',
              padding: '8px 12px',
              borderRadius: msg.role === 'user' ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
              background: msg.role === 'user'
                ? 'rgba(124, 77, 255, 0.2)'
                : 'rgba(255, 255, 255, 0.06)',
              color: '#e8eaf6',
              fontSize: '13px',
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
              textAlign: 'left',
            }}>
              {msg.content}
            </div>
          </div>
        ))}
        {isLoading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#b388ff', fontSize: '13px' }}>
            <Loader2 size={14} className="loading-spinner" style={{ animation: 'spin 1s linear infinite' }} /> Claude denkt nach...
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} style={{
        display: 'flex', gap: '8px', padding: '10px 14px',
        borderTop: '1px solid rgba(124, 77, 255, 0.15)',
      }}>
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Was verstehst du nicht?"
          disabled={isLoading}
          style={{
            flex: 1, padding: '10px 12px',
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(124, 77, 255, 0.2)',
            borderRadius: '8px', color: '#e8eaf6', fontSize: '14px',
            minHeight: '44px',
          }}
        />
        <button
          type="submit"
          disabled={isLoading || !input.trim()}
          style={{
            padding: '10px 14px', background: 'rgba(124, 77, 255, 0.25)',
            border: '1px solid rgba(124, 77, 255, 0.3)', borderRadius: '8px',
            color: '#b388ff', cursor: 'pointer', display: 'flex', alignItems: 'center',
            opacity: isLoading || !input.trim() ? 0.4 : 1,
          }}
        >
          <Send size={16} />
        </button>
      </form>
    </div>
  );
}

// ============ STEP RENDERER ============
function StepView({
  step,
  stepIndex,
  isActive,
  isCompleted,
  isLocked,
  answers,
  onAnswerChange,
  onCheck,
  onFieldFocus,
  attempts,
  fieldResults,
  showExplanation,
}: {
  step: MathStep;
  stepIndex: number;
  isActive: boolean;
  isCompleted: boolean;
  isLocked: boolean;
  answers: Record<string, string>;
  onAnswerChange: (fieldId: string, value: string) => void;
  onCheck: () => void;
  onFieldFocus: (fieldId: string) => void;
  attempts: number;
  fieldResults: Record<string, boolean | null>;
  showExplanation: boolean;
}) {
  const statusIcon = isCompleted ? '✅' : isLocked ? '🔒' : isActive ? '▶️' : '⏳';
  const borderColor = isCompleted ? 'rgba(76, 175, 80, 0.3)' : isActive ? 'rgba(255, 213, 79, 0.3)' : 'rgba(255,255,255,0.06)';
  const bgColor = isCompleted ? 'rgba(76, 175, 80, 0.05)' : isActive ? 'rgba(255, 213, 79, 0.05)' : 'rgba(255,255,255,0.02)';

  return (
    <div style={{
      background: bgColor,
      border: `1px solid ${borderColor}`,
      borderRadius: '12px',
      padding: '14px 16px',
      marginBottom: '10px',
      opacity: isLocked ? 0.4 : 1,
      transition: 'all 0.2s ease',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: isLocked ? 0 : '10px',
      }}>
        <span style={{ fontSize: '14px' }}>{statusIcon}</span>
        <span style={{
          color: isCompleted ? '#4caf50' : isActive ? '#ffd54f' : 'rgba(255,255,255,0.5)',
          fontSize: '13px',
          fontWeight: 600,
        }}>
          {step.label}
        </span>
      </div>

      {!isLocked && (
        <>
          {step.formula && (
            <div style={{
              color: '#e8eaf6',
              fontSize: '14px',
              marginBottom: '8px',
              fontFamily: 'monospace',
            }}>
              {step.formula}
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
            {step.inputFields.map((field) => {
              const result = fieldResults[field.id];
              const isCorrect = result === true;
              const isWrong = result === false;
              const fieldWidth = field.width === 'small' ? '70px' : field.width === 'large' ? '200px' : '120px';

              return (
                <div key={field.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                  {field.prefix && (
                    <span style={{ color: '#9fa8da', fontSize: '13px', fontFamily: 'monospace' }}>{field.prefix}</span>
                  )}
                  <input
                    type="text"
                    inputMode="decimal"
                    value={answers[field.id] || ''}
                    onChange={(e) => onAnswerChange(field.id, e.target.value)}
                    onFocus={() => onFieldFocus(field.id)}
                    disabled={!isActive || isCompleted}
                    placeholder={field.placeholder || '?'}
                    style={{
                      width: fieldWidth,
                      padding: '8px 10px',
                      background: isCorrect ? 'rgba(76, 175, 80, 0.15)' : isWrong ? 'rgba(239, 83, 80, 0.15)' : 'rgba(255,255,255,0.08)',
                      border: `2px solid ${isCorrect ? '#4caf50' : isWrong ? '#ef5350' : isActive ? 'rgba(255, 213, 79, 0.4)' : 'rgba(255,255,255,0.1)'}`,
                      borderRadius: '8px',
                      color: isCorrect ? '#4caf50' : isWrong ? '#ef5350' : '#e8eaf6',
                      fontSize: '15px',
                      fontWeight: 600,
                      textAlign: 'center',
                      fontFamily: 'monospace',
                      minHeight: '44px',
                    }}
                  />
                  {field.suffix && (
                    <span style={{ color: '#9fa8da', fontSize: '13px', fontFamily: 'monospace' }}>{field.suffix}</span>
                  )}
                  {isCorrect && <CheckCircle size={16} style={{ color: '#4caf50' }} />}
                  {isWrong && <XCircle size={16} style={{ color: '#ef5350' }} />}
                </div>
              );
            })}
          </div>

          {isActive && !isCompleted && step.inputFields.length > 0 && (
            <button
              onClick={onCheck}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                width: '100%',
                padding: '10px',
                marginTop: '10px',
                background: 'rgba(255, 213, 79, 0.15)',
                border: '1px solid rgba(255, 213, 79, 0.3)',
                borderRadius: '10px',
                color: '#ffd54f',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <CheckCircle size={16} /> Prüfen
            </button>
          )}

          {showExplanation && step.explanation && (
            <div style={{
              marginTop: '8px',
              padding: '10px 12px',
              background: 'rgba(66, 165, 245, 0.1)',
              border: '1px solid rgba(66, 165, 245, 0.2)',
              borderRadius: '8px',
              color: '#42a5f5',
              fontSize: '13px',
            }}>
              {step.explanation}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ============ MAIN COMPONENT ============
export function MathExercise() {
  const { courseId } = useParams<{ courseId: string }>();
  const [searchParams] = useSearchParams();
  const categoryFilter = searchParams.get('category');
  const weaknessMode = searchParams.get('mode') === 'weakness';
  const { course, isLoading, error } = useCourse(courseId || '');
  const { user } = useAuth();

  const [currentExIndex, setCurrentExIndex] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [stepAttempts, setStepAttempts] = useState<Record<number, number>>({});
  const [stepCompleted, setStepCompleted] = useState<Record<number, boolean>>({});
  const [stepPoints, setStepPoints] = useState<Record<number, number>>({});
  const [fieldResults, setFieldResults] = useState<Record<string, boolean | null>>({});
  const [showExplanation, setShowExplanation] = useState<Record<number, boolean>>({});
  const [showHint, setShowHint] = useState(false);
  const [showAiChat, setShowAiChat] = useState(false);
  const [activeFieldId, setActiveFieldId] = useState<string | null>(null);
  const [isFinished, setIsFinished] = useState(false);
  const [startTime] = useState(Date.now());
  // Weakness tracking
  const [weakExerciseIds, setWeakExerciseIds] = useState<string[]>([]);
  // Per-exercise failure tracking (for saving weaknesses)
  const [exerciseHadErrors, setExerciseHadErrors] = useState<Record<string, boolean>>({});

  // Load weakness data
  useEffect(() => {
    if (!user?.id || !courseId) return;
    (async () => {
      const progress = await getFlashcardProgress(user.id, courseId);
      const weakIds = progress
        .filter(p => p.flashcard_id.startsWith('math_') && !p.knew)
        .map(p => p.flashcard_id.replace('math_', ''));
      setWeakExerciseIds(weakIds);
    })();
  }, [user?.id, courseId]);

  const exercises = useMemo(() => {
    if (!course?.mathExercises) return [];
    let pool = categoryFilter
      ? course.mathExercises.filter(e => e.category === categoryFilter)
      : course.mathExercises;

    if (weaknessMode && weakExerciseIds.length > 0) {
      pool = pool.filter(e => weakExerciseIds.includes(e.id));
    }

    return pool;
  }, [course?.mathExercises, categoryFilter, weaknessMode, weakExerciseIds]);

  const currentExercise = exercises[currentExIndex];

  const resetExerciseState = useCallback(() => {
    setActiveStep(0);
    setAnswers({});
    setStepAttempts({});
    setStepCompleted({});
    setStepPoints({});
    setFieldResults({});
    setShowExplanation({});
    setShowHint(false);
    setShowAiChat(false);
  }, []);

  const handleAnswerChange = useCallback((fieldId: string, value: string) => {
    setAnswers(prev => ({ ...prev, [fieldId]: value }));
    setFieldResults(prev => ({ ...prev, [fieldId]: null }));
  }, []);

  const handleCheckStep = useCallback(() => {
    if (!currentExercise) return;
    const step = currentExercise.steps[activeStep];
    const attempt = (stepAttempts[activeStep] || 0) + 1;

    let allCorrect = true;
    const newResults: Record<string, boolean | null> = { ...fieldResults };

    for (const field of step.inputFields) {
      const userAnswer = answers[field.id] || '';
      const isCorrect = matchMathAnswer(userAnswer, field.correctValue, field.acceptedAnswers);
      newResults[field.id] = isCorrect;
      if (!isCorrect) allCorrect = false;
    }

    setFieldResults(newResults);
    setStepAttempts(prev => ({ ...prev, [activeStep]: attempt }));

    if (allCorrect) {
      playSound('correct');
      const points = attempt === 1 ? 2 : attempt === 2 ? 1 : 0;
      setStepPoints(prev => ({ ...prev, [activeStep]: points }));
      setStepCompleted(prev => ({ ...prev, [activeStep]: true }));

      if (activeStep < currentExercise.steps.length - 1) {
        setTimeout(() => setActiveStep(activeStep + 1), 400);
      } else {
        if (currentExIndex >= exercises.length - 1) {
          playSound('complete');
          setTimeout(() => setIsFinished(true), 600);
        }
      }
    } else {
      playSound('wrong');
      // Track that this exercise had errors
      setExerciseHadErrors(prev => ({ ...prev, [currentExercise.id]: true }));

      if (attempt >= 2) {
        setShowExplanation(prev => ({ ...prev, [activeStep]: true }));
        setStepPoints(prev => ({ ...prev, [activeStep]: 0 }));

        setTimeout(() => {
          const correctedAnswers = { ...answers };
          for (const field of step.inputFields) {
            correctedAnswers[field.id] = field.correctValue;
          }
          setAnswers(correctedAnswers);

          const correctedResults: Record<string, boolean | null> = {};
          for (const field of step.inputFields) {
            correctedResults[field.id] = true;
          }
          setFieldResults(prev => ({ ...prev, ...correctedResults }));
          setStepCompleted(prev => ({ ...prev, [activeStep]: true }));

          if (activeStep < currentExercise.steps.length - 1) {
            setTimeout(() => setActiveStep(activeStep + 1), 500);
          } else if (currentExIndex >= exercises.length - 1) {
            playSound('complete');
            setTimeout(() => setIsFinished(true), 600);
          }
        }, 1500);
      }
    }
  }, [currentExercise, activeStep, answers, fieldResults, stepAttempts, currentExIndex, exercises.length]);

  const handleNextExercise = useCallback(() => {
    if (currentExIndex < exercises.length - 1) {
      setCurrentExIndex(currentExIndex + 1);
      resetExerciseState();
    }
  }, [currentExIndex, exercises.length, resetExerciseState]);

  const allStepsCompleted = currentExercise
    ? currentExercise.steps.every((_, i) => stepCompleted[i])
    : false;

  // Auto-skip formula-only steps
  useEffect(() => {
    if (!currentExercise || isFinished) return;
    const step = currentExercise.steps[activeStep];
    if (!step || step.inputFields.length > 0) return;
    if (stepCompleted[activeStep]) return;

    const timer = setTimeout(() => {
      setStepCompleted(prev => ({ ...prev, [activeStep]: true }));
      setStepPoints(prev => ({ ...prev, [activeStep]: 0 }));

      if (activeStep < currentExercise.steps.length - 1) {
        setActiveStep(activeStep + 1);
      } else if (currentExIndex >= exercises.length - 1) {
        playSound('complete');
        setTimeout(() => setIsFinished(true), 600);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [activeStep, currentExercise, isFinished, stepCompleted, currentExIndex, exercises.length]);

  // Auto-advance to next exercise
  useEffect(() => {
    if (allStepsCompleted && !isFinished && currentExIndex < exercises.length - 1) {
      const timer = setTimeout(handleNextExercise, 1200);
      return () => clearTimeout(timer);
    }
  }, [allStepsCompleted, isFinished, currentExIndex, exercises.length, handleNextExercise]);

  // Log activity + save weakness data when finished
  useEffect(() => {
    if (!isFinished || !user?.id || !courseId) return;
    const totalPoints = Object.values(stepPoints).reduce((a, b) => a + b, 0);
    const totalSteps = exercises.reduce((sum, ex) => sum + ex.steps.filter(s => s.inputFields.length > 0).length, 0);
    const maxPoints = totalSteps * 2;
    const elapsed = Math.round((Date.now() - startTime) / 60000);

    (async () => {
      // Log activity
      await logActivity(user.id, 'math_exercise', {
        course_id: courseId,
        category: categoryFilter || 'all',
        score: totalPoints,
        total: maxPoints,
        percentage: maxPoints > 0 ? Math.round(totalPoints / maxPoints * 100) : 0,
        exercises_completed: exercises.length,
        duration_minutes: elapsed,
        is_weakness_mode: weaknessMode || false,
      });

      // Save per-exercise weakness to flashcard_progress
      for (const ex of exercises) {
        const knew = !exerciseHadErrors[ex.id]; // no errors = knew
        await saveFlashcardResult(user.id, courseId, `math_${ex.id}`, knew);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFinished]);

  // Loading / Error states
  if (isLoading) return <div className="loading">Laden...</div>;
  if (error || !course) {
    return (
      <div className="error-state">
        <p>{error || 'Kurs nicht gefunden'}</p>
        <Link to={`/course/${courseId}`}><ArrowLeft size={18} /> Zurück</Link>
      </div>
    );
  }

  if (weaknessMode && weakExerciseIds.length === 0) {
    return (
      <div style={{ padding: '16px', textAlign: 'center', paddingBottom: '100px' }}>
        <div style={{
          background: 'rgba(76, 175, 80, 0.1)',
          border: '1px solid rgba(76, 175, 80, 0.3)',
          borderRadius: '16px',
          padding: '24px',
          marginTop: '20px',
        }}>
          <div style={{ fontSize: '40px', marginBottom: '8px' }}>🎉</div>
          <h2 style={{ color: '#4caf50', fontSize: '20px', marginBottom: '8px' }}>Keine Schwächen!</h2>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>
            Alle Rechenaufgaben richtig gelöst. Weiter so!
          </p>
        </div>
        <Link to={`/course/${courseId}`} style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          color: '#ffd54f', textDecoration: 'none', marginTop: '16px', fontSize: '14px',
        }}>
          <ArrowLeft size={18} /> Zurück zum Kurs
        </Link>
      </div>
    );
  }

  if (!exercises.length) {
    return (
      <div style={{ padding: '16px', textAlign: 'center' }}>
        <p style={{ color: 'rgba(255,255,255,0.6)' }}>Keine Rechenaufgaben verfügbar.</p>
        <Link to={`/course/${courseId}`} style={{ color: '#ffd54f', textDecoration: 'none' }}>
          <ArrowLeft size={18} style={{ verticalAlign: 'middle' }} /> Zurück zum Kurs
        </Link>
      </div>
    );
  }

  // ============ RESULT SCREEN ============
  if (isFinished) {
    const totalPoints = Object.values(stepPoints).reduce((a, b) => a + b, 0);
    const totalSteps = exercises.reduce((sum, ex) => sum + ex.steps.length, 0);
    const maxPoints = totalSteps * 2;
    const pct = maxPoints > 0 ? Math.round(totalPoints / maxPoints * 100) : 0;
    const stars = pct >= 90 ? 5 : pct >= 75 ? 4 : pct >= 60 ? 3 : pct >= 40 ? 2 : 1;
    const errorCount = Object.values(exerciseHadErrors).filter(Boolean).length;

    return (
      <div style={{ padding: '16px', paddingBottom: '100px' }}>
        <Link to={`/course/${courseId}`} style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          color: '#9fa8da', textDecoration: 'none', marginBottom: '20px',
        }}>
          <ArrowLeft size={18} /> Zurück zum Kurs
        </Link>

        <div style={{
          background: 'rgba(255, 213, 79, 0.08)',
          border: '1px solid rgba(255, 213, 79, 0.2)',
          borderRadius: '16px',
          padding: '24px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '40px', marginBottom: '8px' }}>🎉</div>
          <h2 style={{ color: '#ffd54f', fontSize: '22px', marginBottom: '4px' }}>Geschafft!</h2>
          <div style={{ fontSize: '24px', marginBottom: '12px' }}>
            {'⭐'.repeat(stars)}{'☆'.repeat(5 - stars)}
          </div>
          <div style={{ color: '#e8eaf6', fontSize: '18px', fontWeight: 700 }}>
            {totalPoints} / {maxPoints} Punkte ({pct}%)
          </div>
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', marginTop: '4px' }}>
            {exercises.length} Aufgaben · {Math.round((Date.now() - startTime) / 60000)} Min.
          </div>

          {/* Weakness info */}
          {errorCount > 0 && (
            <div style={{
              marginTop: '12px',
              padding: '10px 14px',
              background: 'rgba(255, 152, 0, 0.1)',
              border: '1px solid rgba(255, 152, 0, 0.2)',
              borderRadius: '10px',
              color: '#ff9800',
              fontSize: '13px',
            }}>
              ⚠️ {errorCount} {errorCount === 1 ? 'Aufgabe' : 'Aufgaben'} mit Fehlern — {errorCount === 1 ? 'kommt' : 'kommen'} beim nächsten Mal bevorzugt
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '20px', flexWrap: 'wrap' }}>
            <button
              onClick={() => {
                setCurrentExIndex(0);
                resetExerciseState();
                setIsFinished(false);
                setExerciseHadErrors({});
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '12px 20px', background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)', borderRadius: '10px',
                color: '#e8eaf6', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
              }}
            >
              <RotateCcw size={16} /> Nochmal üben
            </button>
            <Link to={`/course/${courseId}`} style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '12px 20px', background: '#4caf50',
              border: 'none', borderRadius: '10px',
              color: '#fff', fontSize: '14px', fontWeight: 600, textDecoration: 'none',
            }}>
              Zum Kurs <ChevronRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ============ EXERCISE VIEW ============
  return (
    <div style={{ padding: '16px', paddingBottom: '100px' }}>
      {/* Header */}
      <Link to={`/course/${courseId}`} style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        color: '#9fa8da', textDecoration: 'none', marginBottom: '16px',
      }}>
        <ArrowLeft size={18} /> Zurück zum Kurs
      </Link>

      {/* Weakness mode banner */}
      {weaknessMode && (
        <div style={{
          marginBottom: '12px',
          padding: '10px 14px',
          background: 'rgba(255, 152, 0, 0.1)',
          border: '1px solid rgba(255, 152, 0, 0.2)',
          borderRadius: '10px',
          color: '#ff9800',
          fontSize: '13px',
          fontWeight: 600,
        }}>
          🎯 Schwächen-Modus: {exercises.length} {exercises.length === 1 ? 'Aufgabe' : 'Aufgaben'} mit Fehlern
        </div>
      )}

      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px',
      }}>
        <span style={{ color: '#ffd54f', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>
          Aufgabe {currentExIndex + 1} von {exercises.length}
        </span>
        <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>
          {currentExercise.title}
        </span>
      </div>

      {/* Progress bar */}
      <div style={{
        height: '4px', background: 'rgba(255,255,255,0.08)', borderRadius: '2px', marginBottom: '16px',
      }}>
        <div style={{
          height: '100%', background: '#ffd54f', borderRadius: '2px', transition: 'width 0.3s ease',
          width: `${((currentExIndex + (allStepsCompleted ? 1 : 0)) / exercises.length) * 100}%`,
        }} />
      </div>

      {/* Instruction */}
      <div style={{
        background: 'rgba(255, 213, 79, 0.08)',
        border: '1px solid rgba(255, 213, 79, 0.2)',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '16px',
      }}>
        <div style={{
          color: '#ffd54f', fontSize: '15px', fontWeight: 600, lineHeight: 1.5,
          whiteSpace: 'pre-line',
        }}>
          {currentExercise.instruction}
        </div>
      </div>

      {/* Diagram */}
      {currentExercise.diagram && (
        <MathDiagramRenderer diagram={currentExercise.diagram} />
      )}

      {/* Steps */}
      {currentExercise.steps.map((step, i) => (
        <StepView
          key={step.id}
          step={step}
          stepIndex={i}
          isActive={activeStep === i}
          isCompleted={!!stepCompleted[i]}
          isLocked={i > activeStep && !stepCompleted[i]}
          answers={answers}
          onAnswerChange={handleAnswerChange}
          onCheck={handleCheckStep}
          onFieldFocus={setActiveFieldId}
          attempts={stepAttempts[i] || 0}
          fieldResults={fieldResults}
          showExplanation={!!showExplanation[i]}
        />
      ))}

      {/* Math Keyboard */}
      {!isFinished && (
        <MathKeyboard
          onInsert={(symbol) => {
            if (activeFieldId) {
              const current = answers[activeFieldId] || '';
              handleAnswerChange(activeFieldId, current + symbol);
            }
          }}
          onTransfer={(value) => {
            if (activeFieldId) {
              handleAnswerChange(activeFieldId, value);
            }
          }}
        />
      )}

      {/* Hint button */}
      {currentExercise.hint && !showHint && (
        <button
          onClick={() => setShowHint(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px', width: '100%',
            padding: '10px', marginTop: '8px', background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px',
            color: 'rgba(255,255,255,0.5)', fontSize: '13px', cursor: 'pointer',
          }}
        >
          <HelpCircle size={16} /> Hilfe anzeigen
        </button>
      )}
      {showHint && currentExercise.hint && (
        <div style={{
          marginTop: '8px', padding: '12px', background: 'rgba(255, 152, 0, 0.1)',
          border: '1px solid rgba(255, 152, 0, 0.2)', borderRadius: '10px',
          color: '#ff9800', fontSize: '13px', lineHeight: 1.5,
        }}>
          💡 {currentExercise.hint}
        </div>
      )}

      {/* Frag Claude button */}
      {!showAiChat && (
        <button
          onClick={() => setShowAiChat(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px', width: '100%',
            padding: '10px', marginTop: '8px',
            background: 'rgba(124, 77, 255, 0.08)',
            border: '1px solid rgba(124, 77, 255, 0.2)',
            borderRadius: '10px',
            color: '#b388ff', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
          }}
        >
          <MessageCircle size={16} /> Frag Claude — ich erklär dir das!
        </button>
      )}

      {/* AI Chat Panel */}
      {showAiChat && user?.id && courseId && currentExercise && (
        <AiChatPanel
          exercise={currentExercise}
          courseId={courseId}
          userId={user.id}
          onClose={() => setShowAiChat(false)}
        />
      )}
    </div>
  );
}
